import { useMemo, useState } from 'react';
import { InlineStack, Select } from '@customerio/pluma-components/react';
import { fieldError } from '@customerio/dryad';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { useAppsOptional } from '@/services/db/app/queries';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import { useLiveNotificationTypesOptional } from '@/services/db/live-notification-type/queries';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { useBuilderActions, useLeafState, useMeta, useNodeErrors } from '@customerio/dryad-react';
import type { ReactNode } from 'react';
import { clearsValues, isUnaryFilterOperator, type AttributeFilterState } from '../attribute-filter-state';
import { groupsForFamilies, type AttributeFieldPolicy, type GroupableFamily } from '../operator-groups';
import { isOperatorInFamilies } from '../operators';
import { OperatorValue } from '../components/operator-value/operator-value';
import {
  LIVE_NOTIFICATION_MARKER_PREFIX,
  appConditionOptions,
  lastStatusPatchFor,
  liveNotificationMarkerOptions,
  specialDeviceAttributeConfig,
  specialDeviceAttributeOf,
  SPECIAL_DEVICE_ATTRIBUTES,
  type SpecialDeviceAttributeConfig,
} from './device-attributes';
import { isEmptyString } from '@/utils/string';

type DeviceAttributeRowProps = {
  /** The leaf node's id, supplied by the group to every registered view. */
  id: string;
  /** The plugin type gating the typed read — the surface's device leaf. */
  conditionType: string;
  /** The condition-type select, where the surface offers more than one type. */
  typeSelect?: ReactNode;
  /** The operator families this surface declares for device conditions. */
  operatorFamilies: readonly GroupableFamily[];
  /**
   * The surface's field rules. Only the oneOf and email families read it, and
   * no surface declares those here today — passed so one that does keeps its
   * disabled-operator rules instead of silently losing them.
   */
  fieldPolicy?: AttributeFieldPolicy;
};

type AttributeNameOption = {
  value: string;
  label: string;
  isDisabled?: boolean;
  description?: string;
  icon?: 'apple' | 'android';
};

/** The value select mixes app options (which carry a description) with the
 * special attributes' plain label/value pairs, so it holds the wider shape. */
type DeviceValueOption = {
  value: string;
  label: string;
  description?: string;
};

// Device attributes have no value-suggestion source; the free-text editor
// renders with an empty option list.
const NO_VALUE_OPTIONS: { value: string; label: string }[] = [];

function useDeviceAttributeNameOptions(
  currentField: string,
  isMultiAppEnabled: boolean,
  isAppCondition: boolean,
  appOptionsCount: number,
  isLoadingApps: boolean,
) {
  const workspaceId = requireWorkspaceId();
  const environment = useEnvironmentOptional();
  const deviceAttributes = useMemo(() => environment.data?.environment?.device_attributes ?? [], [environment.data]);

  const hasMarkers = deviceAttributes.some((name) => name.startsWith(LIVE_NOTIFICATION_MARKER_PREFIX));
  const liveNotificationTypes = useLiveNotificationTypesOptional(workspaceId, { enabled: hasMarkers });
  const registrations = useMemo(() => liveNotificationTypes.data ?? [], [liveNotificationTypes.data]);

  // Special attributes offered, mirroring the picker this replaces:
  // `_last_used` is excluded (its timestamp operators aren't servable on this
  // surface), and `app_identifier` is offered only with multi_app enabled or
  // when the condition already targets an app, kept visible but disabled
  // while apps load or when none are configured.
  // fallow-ignore-next-line complexity -- one exclusion rule per special attribute
  return useMemo<AttributeNameOption[]>(() => {
    const specialOptions: AttributeNameOption[] = [];
    for (const value of SPECIAL_DEVICE_ATTRIBUTES) {
      if (value === '_last_used') {
        continue;
      }
      if (value === 'app_identifier' && !isMultiAppEnabled && !isAppCondition) {
        continue;
      }
      const option: AttributeNameOption = { value, label: specialDeviceAttributeConfig(value).label };
      if (value === 'app_identifier') {
        if (isLoadingApps) {
          option.isDisabled = true;
          option.description = 'Loading apps…';
        } else if (appOptionsCount === 0) {
          option.isDisabled = true;
          option.description = 'No mobile apps configured';
        }
      }
      specialOptions.push(option);
    }

    // Live-notification markers ("All types" pinned when present, the rest
    // alphabetical), then the workspace's raw device attributes.
    const markerNames: string[] = [];
    const otherOptions: AttributeNameOption[] = [];
    for (const name of deviceAttributes) {
      if (specialDeviceAttributeOf(name) != null) {
        continue;
      }
      if (name.startsWith(LIVE_NOTIFICATION_MARKER_PREFIX)) {
        markerNames.push(name);
      } else {
        otherOptions.push({ value: name, label: name });
      }
    }

    const options = [...specialOptions, ...liveNotificationMarkerOptions(markerNames, registrations), ...otherOptions];

    // A stored condition can hold an attribute the picker no longer offers.
    // Keep it selectable so the select doesn't render empty.
    if (currentField !== '' && !options.some((option) => option.value === currentField)) {
      const special = specialDeviceAttributeOf(currentField);
      options.push({
        value: currentField,
        label: special == null ? currentField : specialDeviceAttributeConfig(special).label,
      });
    }
    return options;
  }, [
    deviceAttributes,
    registrations,
    currentField,
    isMultiAppEnabled,
    isAppCondition,
    appOptionsCount,
    isLoadingApps,
  ]);
}

/**
 * A device-attribute condition row: [type] [attribute name] [operator]
 * [value…]. Special attributes come with their own operator list and (when
 * configured) a fixed value select; everything else gets the surface's
 * declared operator families and free text.
 */
// fallow-ignore-next-line complexity -- the special-attribute editor forks of the editor this replaces
export function DeviceAttributeRow({
  id,
  conditionType,
  typeSelect,
  operatorFamilies,
  fieldPolicy,
}: DeviceAttributeRowProps) {
  const meta = useMeta();
  const { state, node } = useLeafState<AttributeFilterState>(id, conditionType);
  const { updateState } = useBuilderActions();
  const errors = useNodeErrors(id);

  const [fieldTouched, setFieldTouched] = useState(() => state.field !== '');
  const [valueTouched, setValueTouched] = useState(() => !isEmptyString(state.value));
  const fieldErrorMessage = fieldTouched ? fieldError(errors, 'field') : undefined;
  const valueErrorMessage = valueTouched ? fieldError(errors, 'value') : undefined;

  const isMultiAppEnabled = useFeatureFlag(AccountFeatureFlag.MultiApp);
  const isAppCondition = state.field === 'app_identifier';
  // Load apps when the multi-app filter is available, or when editing a
  // condition that already targets an app (authored before the flag toggled off).
  const apps = useAppsOptional({ enabled: isMultiAppEnabled || isAppCondition });
  const appOptions = useMemo(() => appConditionOptions(apps.data ?? []), [apps.data]);

  const attributeNameOptions = useDeviceAttributeNameOptions(
    state.field,
    isMultiAppEnabled,
    isAppCondition,
    appOptions.length,
    apps.isLoading,
  );

  const specialAttribute = specialDeviceAttributeOf(state.field);
  const specialConfig: SpecialDeviceAttributeConfig | null =
    specialAttribute == null ? null : specialDeviceAttributeConfig(specialAttribute);

  const isLastStatusCondition = state.field === '_last_status';
  // The stored wire encodes `_last_status` "unused" as an existence operator,
  // never as a value: !exists ≡ is unused, exists ≡ is not unused. The row
  // mirrors that codec — the selects display eq/!eq + 'unused' while the
  // state holds the existence operator and no value.
  const isLastStatusUnused = isLastStatusCondition && isUnaryFilterOperator(state.operator);

  const usesConfiguredValueOptions = isAppCondition || (specialConfig != null && specialConfig.values.length > 0);

  // The attribute's configured operators, restricted to the surface's declared
  // families so a stored condition can never be edited onto an operator the
  // plugin's claim would reject (e.g. `_last_used`'s timestamp operators).
  const declaredFamilies = new Set(operatorFamilies);
  const specialOperatorOptions = (specialConfig?.operators ?? []).filter((option) =>
    isOperatorInFamilies(option.value, declaredFamilies),
  );

  const selectedOperator = isLastStatusUnused ? (state.operator === '!exists' ? 'eq' : '!eq') : state.operator;
  const selectedValue = isLastStatusUnused ? 'unused' : (state.value ?? undefined);
  const showValueSelect = isLastStatusCondition || !isUnaryFilterOperator(state.operator);

  // fallow-ignore-next-line complexity -- config values plus the keep-stored-value fallback
  const valueSelectOptions = useMemo(() => {
    // Widened to the shared label shape: app options carry a `description` the
    // special-attribute values don't, and the keep-stored-value fallback below
    // pushes a bare value/label pair onto whichever list this resolved to.
    const options: DeviceValueOption[] = isAppCondition ? [...appOptions] : [...(specialConfig?.values ?? [])];
    // A stored condition can hold a value the list no longer offers; keep it
    // selectable. Not on a unary operator: `_last_status` is the one field
    // whose select still renders there, and the value it holds is carried
    // only so the wire stays byte-identical — offering it would put a string
    // no producer meant as a status next to bounced/suppressed/sent/unused.
    const { value } = state;
    if (
      value != null &&
      value !== '' &&
      !isUnaryFilterOperator(state.operator) &&
      !options.some((option) => option.value === value)
    ) {
      options.push({ value, label: value });
    }
    return options;
  }, [isAppCondition, appOptions, specialConfig, state]);

  const patch = (partial: Partial<AttributeFilterState>) => {
    if (node != null) {
      updateState(node, partial);
    }
  };

  // Picking an attribute resets operator and value to the attribute's
  // defaults: a special attribute brings its configured defaults, an app
  // condition defaults to the first configured app, and a unary default
  // operator carries no value.
  // fallow-ignore-next-line complexity -- per-attribute default rules of the picker this replaces
  const handleAttributeChange = (value: string | null) => {
    if (value == null || value === '') {
      return;
    }
    let operator = 'eq';
    let nextValue: string | null = null;
    const special = specialDeviceAttributeOf(value);
    if (special != null) {
      const config = specialDeviceAttributeConfig(special);
      operator = config.defaultOperator;
      nextValue = config.defaultValue ?? null;
    }
    if (value === 'app_identifier') {
      nextValue = appOptions[0]?.value ?? null;
    }
    if (isUnaryFilterOperator(operator)) {
      nextValue = null;
    }
    patch({ field: value, operator, value: nextValue, value2: null });
    setValueTouched(!isEmptyString(nextValue));
  };

  const handleOperatorChange = (operator: string | null) => {
    if (operator == null) {
      return;
    }
    if (isLastStatusUnused) {
      patch({ operator: operator === 'eq' ? '!exists' : 'exists', value: null, value2: null });
      return;
    }
    if (clearsValues(operator, state.operator)) {
      setValueTouched(false);
      patch({ operator, value: null, value2: null });
      return;
    }
    patch({ operator, value2: null });
  };

  const handleValueChange = (value: string | null) => {
    if (value == null) {
      return;
    }
    setValueTouched(true);
    if (isLastStatusCondition) {
      patch(lastStatusPatchFor(state, value));
      return;
    }
    patch({ value });
  };

  return (
    <InlineStack data-testid="dryad-condition-row" gap="050" alignBlock="baseline" wrap>
      {typeSelect}
      <Select
        data-testid="dryad-device-attribute-name"
        ariaLabel="Device attribute"
        size="sm"
        value={state.field === '' ? undefined : state.field}
        options={attributeNameOptions}
        placeholder="Select a device attribute"
        isSearchable
        error={fieldErrorMessage}
        onChange={handleAttributeChange}
        onOpenChange={(isOpen: boolean) => {
          if (!isOpen) {
            setFieldTouched(true);
          }
        }}
        isDisabled={meta.isDisabled}
      />
      {usesConfiguredValueOptions ? (
        <>
          <Select
            data-testid="dryad-device-attribute-operator"
            ariaLabel="Operator"
            size="sm"
            options={specialOperatorOptions}
            value={selectedOperator}
            onChange={handleOperatorChange}
            isDisabled={meta.isDisabled}
          />
          {showValueSelect ? (
            <Select
              data-testid="dryad-device-attribute-value"
              ariaLabel="Value"
              size="sm"
              options={valueSelectOptions}
              value={selectedValue}
              placeholder="Select a value"
              error={valueErrorMessage}
              onChange={handleValueChange}
              onOpenChange={(isOpen: boolean) => {
                if (!isOpen) {
                  setValueTouched(true);
                }
              }}
              isDisabled={meta.isDisabled}
            />
          ) : null}
        </>
      ) : (
        <OperatorValue
          field={state.field}
          operator={state.operator}
          value={state.value}
          value2={state.value2}
          valueOptions={NO_VALUE_OPTIONS}
          operatorGroups={groupsForFamilies(operatorFamilies, state.field, fieldPolicy)}
          disabled={meta.isDisabled}
          errors={errors}
          onChange={patch}
        />
      )}
    </InlineStack>
  );
}
